import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { SxProps, Theme } from '@mui/material/styles';
import { FC, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useConfigContext } from '../../context/ConfigContext';
import Logo from '../logo/Logo';
import { RoutePath } from '../route/type';
import { isExternalRoute, isInternalRoute, navigateExternalRoute } from '../route/utils';
import MobileButton from './MobileButton';
import MobileMenuItem from './MobileMenuItem';

const SX_BOX: SxProps<Theme> = {
  display: { lg: 'none', xs: 'flex' },
  flexGrow: 0,
};

const SX_MENU: SxProps<Theme> = {
  display: { lg: 'none', xs: 'block' },
};

const SX_PAPER: SxProps<Theme> = {
  '.MuiMenuItem-root:hover': {
    backgroundColor: 'transparent',
  },
  backgroundColor: 'var(--bg-mobile-menu)',
  backgroundImage: 'none',
  borderRadius: 0,
  boxShadow: '0 0 64px var(--nav-shadow)',
  height: 'auto',
  left: 0,
  maxHeight: '100%',
  maxWidth: '100%',
  overflow: 'hidden',
  position: 'fixed',
  top: 0,
  transformOrigin: '0px 0px',
  transition: 'var(--transition-ease-out-250)',
  width: '100%',
};

const SX_MENU_ITEM: SxProps<Theme> = {
  height: 'var(--logo-m)',
  marginBottom: 'calc(var(--logo) - var(--logo-m))',
};

const MobileMenu: FC = () => {
  const { menu } = useConfigContext();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const navigate = useNavigate();

  const handleOpen = useCallback(
    (event: React.MouseEvent<HTMLElement, MouseEvent>) => setAnchorEl(event.currentTarget),
    []
  );

  const handleClose = useCallback(() => setAnchorEl(null), []);

  const handleSelect = useCallback(
    (href: RoutePath | string) => {
      handleClose();

      if (isInternalRoute(href)) {
        navigate(href);
      } else if (isExternalRoute(href)) {
        navigateExternalRoute(href);
      }
    },
    [handleClose, navigate]
  );

  useEffect(() => {
    window.addEventListener('resize', handleClose);
    return () => {
      window.removeEventListener('resize', handleClose);
    };
  }, [handleClose]);

  return (
    <Box sx={SX_BOX}>
      <MobileButton mode='open' onClick={handleOpen} />

      <Menu
        anchorEl={anchorEl}
        anchorPosition={{ left: 0, top: 0 }}
        anchorReference='none'
        component='nav'
        disableScrollLock
        id='menu-appbar'
        keepMounted
        MenuListProps={{ disablePadding: true }}
        onClose={handleClose}
        open={Boolean(anchorEl)}
        slotProps={{
          paper: { sx: SX_PAPER },
        }}
        sx={SX_MENU}
      >
        <MenuItem disableGutters disableRipple sx={SX_MENU_ITEM}>
          <MobileButton mode='close' onClick={handleClose} />
          <Logo />
        </MenuItem>

        {menu.map(({ href, text }) => (
          <MobileMenuItem href={href} key={text} onClick={handleSelect} text={text} />
        ))}
      </Menu>
    </Box>
  );
};

export default MobileMenu;
